import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { Camera, Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { ChoiceGroup } from '../../components/Choice';
import { Field, Input, Select, Textarea } from '../../components/Field';
import { AttachmentImage } from '../../components/AttachmentImage';
import { useDb } from '../../db/hooks';
import { ACCESS_LABELS, useProfile, type AccessType } from '../../data/profile';
import { useSessions } from '../../data/sessions';
import {
  ACCESS_SIGN_LABELS,
  ACCESS_SIGNS,
  deleteEntry,
  FLUID_KIND_LABELS,
  saveEntry,
  SEVERITY_LABELS,
  SYMPTOMS,
  useAttachments,
  useLastEntry,
  type AccessCheck,
  type AccessSign,
  type BpEntry,
  type FluidEntry,
  type FluidKind,
  type SymptomEntry,
  type TrackingTable,
  type WeightEntry
} from '../../data/tracking';
import { inputValue } from '../../lib/forms';
import { downscalePhoto } from '../../lib/photos';
import { toLocalInput } from '../../lib/time';
import { BloodPressureFields } from '../sessions/VitalsFields';
import { localToday } from '../sessions/fields';
import { accessSchema, bpSchema, fluidSchema, signValue, symptomSchema, weightSchema } from './fields';

export interface EntryFormProps<T> {
  entry?: T;
  onDone: () => void;
}

function Footer({
  table,
  id,
  submitting,
  error,
  onDone
}: {
  table: TrackingTable;
  id?: string;
  submitting: boolean;
  error?: string;
  onDone: () => void;
}) {
  const db = useDb();
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="space-y-2 pt-1">
      {error ? <p className="text-sm text-clinical-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting ? 'Saving…' : 'Save'}
      </Button>
      {id ? (
        confirm ? (
          <div className="flex items-center gap-2">
            <span className="flex-1 text-sm">Delete this entry?</span>
            <Button type="button" variant="danger" onClick={() => void deleteEntry(db, table, id).then(onDone)}>
              Delete
            </Button>
            <Button type="button" variant="secondary" onClick={() => setConfirm(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button type="button" variant="ghost" className="w-full !text-clinical-danger hover:!bg-red-500/10" onClick={() => setConfirm(true)}>
            Delete entry
          </Button>
        )
      ) : null}
    </div>
  );
}

/** Wraps a save so a database error (e.g. read-only plan) shows on the form instead of being lost. */
function useSubmit<T>(form: { setError: (name: 'root', error: { message: string }) => void }, save: (values: T) => Promise<void>) {
  return async (values: T) => {
    try {
      await save(values);
    } catch (error) {
      form.setError('root', { message: error instanceof Error ? error.message : String(error) });
    }
  };
}

/* ------------------------------------------------------------------ Fluid */

const FLUID_AMOUNTS = [100, 150, 200, 250, 330, 500];

export function FluidForm({ entry, onDone }: EntryFormProps<FluidEntry>) {
  const db = useDb();
  const last = useLastEntry<FluidEntry>('fluid_entries', 'taken_at');
  const form = useForm<z.input<typeof fluidSchema>, unknown, z.output<typeof fluidSchema>>({
    resolver: zodResolver(fluidSchema),
    defaultValues: {
      taken_at: toLocalInput(entry?.taken_at),
      amount_ml: inputValue(entry?.amount_ml),
      kind: entry?.kind ?? last?.kind ?? 'water',
      note: inputValue(entry?.note)
    }
  });
  const { errors, isSubmitting } = form.formState;
  const submit = useSubmit(form, async (values: z.output<typeof fluidSchema>) => {
    await saveEntry(db, 'fluid_entries', entry?.id, values);
    onDone();
  });

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)} className="space-y-4">
      <div>
        <Field label="Amount (mL)" error={errors.amount_ml?.message}>
          <Input inputMode="numeric" autoFocus={!entry} {...form.register('amount_ml')} />
        </Field>
        <div className="mt-2 grid grid-cols-6 gap-1.5">
          {FLUID_AMOUNTS.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => form.setValue('amount_ml', String(amount), { shouldDirty: true, shouldValidate: true })}
              className="focus-ring min-h-11 rounded-lg border border-clinical-line text-xs font-semibold text-clinical-primary hover:bg-clinical-surface"
            >
              {amount}
            </button>
          ))}
        </div>
      </div>
      <Controller
        control={form.control}
        name="kind"
        render={({ field }) => (
          <ChoiceGroup<FluidKind>
            name="fluid-kind"
            value={field.value}
            onChange={field.onChange}
            columns={2}
            options={(Object.keys(FLUID_KIND_LABELS) as FluidKind[]).map((value) => ({ value, label: FLUID_KIND_LABELS[value] }))}
          />
        )}
      />
      <Field label="When" error={errors.taken_at?.message}>
        <Input type="datetime-local" {...form.register('taken_at')} />
      </Field>
      <Field label="Note (optional)" error={errors.note?.message}>
        <Input {...form.register('note')} />
      </Field>
      <Footer table="fluid_entries" id={entry?.id} submitting={isSubmitting} error={errors.root?.message} onDone={onDone} />
    </form>
  );
}

/* ------------------------------------------------------------------ Weight */

export function WeightForm({ entry, onDone }: EntryFormProps<WeightEntry>) {
  const db = useDb();
  const last = useLastEntry<WeightEntry>('weight_entries', 'measured_at');
  const form = useForm<z.input<typeof weightSchema>, unknown, z.output<typeof weightSchema>>({
    resolver: zodResolver(weightSchema),
    defaultValues: {
      measured_at: toLocalInput(entry?.measured_at),
      weight_kg: inputValue(entry?.weight_kg),
      context: entry?.context ?? last?.context ?? 'morning'
    }
  });
  const { errors, isSubmitting } = form.formState;
  const submit = useSubmit(form, async (values: z.output<typeof weightSchema>) => {
    await saveEntry(db, 'weight_entries', entry?.id, values);
    onDone();
  });

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)} className="space-y-4">
      <Field label="Weight (kg)" help={last && !entry ? `Last time: ${last.weight_kg} kg` : undefined} error={errors.weight_kg?.message}>
        <Input inputMode="decimal" autoFocus={!entry} {...form.register('weight_kg')} />
      </Field>
      <Controller
        control={form.control}
        name="context"
        render={({ field }) => (
          <ChoiceGroup<'morning' | 'other'>
            name="weight-context"
            value={field.value}
            onChange={field.onChange}
            columns={2}
            options={[
              { value: 'morning', label: 'Morning weight', description: 'After the toilet, before eating' },
              { value: 'other', label: 'Other time' }
            ]}
          />
        )}
      />
      <Field label="When" error={errors.measured_at?.message}>
        <Input type="datetime-local" {...form.register('measured_at')} />
      </Field>
      <p className="text-xs text-clinical-muted">Weights from your sessions are added automatically; log weights between sessions here.</p>
      <Footer table="weight_entries" id={entry?.id} submitting={isSubmitting} error={errors.root?.message} onDone={onDone} />
    </form>
  );
}

/* ------------------------------------------------------------------ Blood pressure */

export function BpForm({ entry, onDone }: EntryFormProps<BpEntry>) {
  const db = useDb();
  const form = useForm<z.input<typeof bpSchema>, unknown, z.output<typeof bpSchema>>({
    resolver: zodResolver(bpSchema),
    defaultValues: {
      measured_at: toLocalInput(entry?.measured_at),
      bp_systolic: inputValue(entry?.systolic),
      bp_diastolic: inputValue(entry?.diastolic),
      pulse: inputValue(entry?.pulse)
    }
  });
  const { errors, isSubmitting } = form.formState;
  const submit = useSubmit(form, async ({ measured_at, bp_systolic, bp_diastolic, pulse }: z.output<typeof bpSchema>) => {
    await saveEntry<BpEntry>(db, 'bp_entries', entry?.id, { measured_at, systolic: bp_systolic!, diastolic: bp_diastolic!, pulse });
    onDone();
  });

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)} className="space-y-4">
      <BloodPressureFields register={form.register} errors={errors} />
      <Field label="When" error={errors.measured_at?.message}>
        <Input type="datetime-local" {...form.register('measured_at')} />
      </Field>
      <p className="text-xs text-clinical-muted">For readings at home. Readings at dialysis go in the session.</p>
      <Footer table="bp_entries" id={entry?.id} submitting={isSubmitting} error={errors.root?.message} onDone={onDone} />
    </form>
  );
}

/* ------------------------------------------------------------------ Symptom */

/** `sessionId`: logging from a session's During step, so the symptom belongs to that session. */
export function SymptomForm({ entry, sessionId: forSession, onDone }: EntryFormProps<SymptomEntry> & { sessionId?: string }) {
  const db = useDb();
  const { sessions } = useSessions();
  // A session running now, or today's, is the one a symptom would have happened during.
  const current = sessions.find((session) => session.status === 'in_progress') ?? sessions.find((session) => session.session_date === localToday() && session.status !== 'missed');
  const sessionId = forSession ?? entry?.during_session_id ?? current?.id ?? null;
  const form = useForm<z.input<typeof symptomSchema>, unknown, z.output<typeof symptomSchema>>({
    resolver: zodResolver(symptomSchema),
    defaultValues: {
      occurred_at: toLocalInput(entry?.occurred_at),
      symptom: entry?.symptom ?? '',
      severity: entry?.severity ?? 3,
      during_session: entry ? entry.during_session_id != null : forSession != null || current?.status === 'in_progress',
      note: inputValue(entry?.note)
    }
  });
  const { errors, isSubmitting } = form.formState;
  const symptom = form.watch('symptom');
  const severity = form.watch('severity');
  const custom = symptom !== '' && !(SYMPTOMS as readonly string[]).includes(symptom);
  const [typing, setTyping] = useState(custom);
  const submit = useSubmit(form, async ({ during_session, ...values }: z.output<typeof symptomSchema>) => {
    await saveEntry(db, 'symptom_entries', entry?.id, { ...values, during_session_id: during_session ? sessionId : null });
    onDone();
  });

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)} className="space-y-4">
      <div>
        <span className="mb-1 block text-sm font-medium">Symptom</span>
        <div role="radiogroup" aria-label="Symptom" className="flex flex-wrap gap-1.5">
          {SYMPTOMS.map((name) => (
            <button
              key={name}
              type="button"
              role="radio"
              aria-checked={symptom === name}
              onClick={() => {
                setTyping(false);
                form.setValue('symptom', name, { shouldValidate: true });
              }}
              className={`focus-ring min-h-10 rounded-full border px-3 text-sm ${
                symptom === name ? 'border-clinical-primary bg-clinical-primary text-white' : 'border-clinical-line bg-clinical-card'
              }`}
            >
              {name}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setTyping(true);
              form.setValue('symptom', custom ? symptom : '');
            }}
            className={`focus-ring min-h-10 rounded-full border px-3 text-sm ${typing ? 'border-clinical-primary text-clinical-primary' : 'border-dashed border-clinical-line'}`}
          >
            Something else
          </button>
        </div>
        {typing ? <Input className="mt-2" aria-label="Other symptom" placeholder="Type the symptom" {...form.register('symptom')} /> : null}
        {errors.symptom?.message ? <p className="mt-1 text-xs text-clinical-danger">{errors.symptom.message}</p> : null}
      </div>
      <div>
        <span className="mb-1 block text-sm font-medium">How bad: {SEVERITY_LABELS[severity]}</span>
        <div role="radiogroup" aria-label="How bad" className="grid grid-cols-5 gap-1.5">
          {[1, 2, 3, 4, 5].map((level) => (
            <button
              key={level}
              type="button"
              role="radio"
              aria-checked={severity === level}
              aria-label={`${level}: ${SEVERITY_LABELS[level]}`}
              onClick={() => form.setValue('severity', level)}
              className={`focus-ring min-h-11 rounded-lg border text-sm font-semibold ${
                severity === level ? 'border-clinical-primary bg-clinical-primary text-white' : 'border-clinical-line bg-clinical-card'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>
      <Field label="When" error={errors.occurred_at?.message}>
        <Input type="datetime-local" {...form.register('occurred_at')} />
      </Field>
      {sessionId ? (
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <input type="checkbox" className="h-5 w-5 accent-[rgb(var(--rf-primary))]" {...form.register('during_session')} />
          During dialysis that day
        </label>
      ) : null}
      <Field label="Note (optional)" error={errors.note?.message}>
        <Textarea {...form.register('note')} />
      </Field>
      <Footer table="symptom_entries" id={entry?.id} submitting={isSubmitting} error={errors.root?.message} onDone={onDone} />
    </form>
  );
}

/* ------------------------------------------------------------------ Access check */

function SignToggle({ value, onChange, label }: { value: 'yes' | 'no' | ''; onChange: (value: 'yes' | 'no' | '') => void; label: string }) {
  return (
    <div className="flex items-center gap-2 py-1.5">
      <span className="flex-1 text-sm">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex gap-1">
        {(['yes', 'no'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={value === option}
            onClick={() => onChange(value === option ? '' : option)}
            className={`focus-ring min-h-10 w-14 rounded-lg border text-sm font-medium ${
              value === option ? 'border-clinical-primary bg-clinical-primary text-white' : 'border-clinical-line bg-clinical-card text-clinical-muted'
            }`}
          >
            {option === 'yes' ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AccessCheckForm({ entry, onDone }: EntryFormProps<AccessCheck>) {
  const db = useDb();
  const { profile } = useProfile();
  const photos = useAttachments('access_checks').get(entry?.id ?? '') ?? [];
  const [newPhotos, setNewPhotos] = useState<{ key: string; bytes: Uint8Array<ArrayBuffer>; url: string }[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const form = useForm<z.input<typeof accessSchema>, unknown, z.output<typeof accessSchema>>({
    resolver: zodResolver(accessSchema),
    defaultValues: {
      checked_at: toLocalInput(entry?.checked_at),
      access_type: entry?.access_type ?? profile?.access_type ?? '',
      ...(Object.fromEntries(ACCESS_SIGNS.map((sign) => [sign, signValue(entry?.[sign])])) as Record<AccessSign, 'yes' | 'no' | ''>),
      note: inputValue(entry?.note)
    }
  });
  const { errors, isSubmitting } = form.formState;
  const accessType = form.watch('access_type');
  const signs = ACCESS_SIGNS.filter((sign) => accessType !== 'catheter' || (sign !== 'thrill_present' && sign !== 'bruit_present'));

  async function addPhotos(fileList: FileList | null) {
    setPhotoError(null);
    for (const file of Array.from(fileList ?? [])) {
      try {
        const bytes = await downscalePhoto(file);
        setNewPhotos((current) => [...current, { key: crypto.randomUUID(), bytes, url: URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' })) }]);
      } catch (error) {
        setPhotoError(error instanceof Error ? error.message : String(error));
      }
    }
  }

  const submit = useSubmit(form, async (values: z.output<typeof accessSchema>) => {
    const saved = await saveEntry<AccessCheck>(db, 'access_checks', entry?.id, values);
    for (const photo of newPhotos) {
      await db.putAttachment({ ownerTable: 'access_checks', ownerId: saved.id, mime: 'image/jpeg', fileName: null, bytes: photo.bytes });
      URL.revokeObjectURL(photo.url);
    }
    onDone();
  });

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)} className="space-y-4">
      <Field label="Access">
        <Select {...form.register('access_type')}>
          <option value="">Not set</option>
          {(Object.keys(ACCESS_LABELS) as AccessType[]).map((type) => (
            <option key={type} value={type}>
              {ACCESS_LABELS[type]}
            </option>
          ))}
        </Select>
      </Field>
      <div className="divide-y divide-clinical-line rounded-lg border border-clinical-line px-3">
        {signs.map((sign) => (
          <Controller
            key={sign}
            control={form.control}
            name={sign}
            render={({ field }) => <SignToggle label={ACCESS_SIGN_LABELS[sign]} value={field.value} onChange={field.onChange} />}
          />
        ))}
      </div>
      <div>
        <span className="mb-1 block text-sm font-medium">Photos</span>
        <div className="grid grid-cols-4 gap-2">
          {photos.map((photo) => (
            <div key={photo.id} className="relative">
              <AttachmentImage attachment={photo} alt="Access photo" className="aspect-square w-full rounded-lg" />
              <button
                type="button"
                aria-label="Remove photo"
                onClick={() => void db.deleteAttachment(photo.id)}
                className="focus-ring absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {newPhotos.map((photo) => (
            <div key={photo.key} className="relative">
              <img src={photo.url} alt="New access photo" className="aspect-square w-full rounded-lg object-cover" />
              <button
                type="button"
                aria-label="Remove photo"
                onClick={() => setNewPhotos((current) => current.filter((item) => item.key !== photo.key))}
                className="focus-ring absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <label className="focus-within:ring-2 flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-clinical-line text-xs text-clinical-muted hover:bg-clinical-surface">
            <Camera className="h-5 w-5" />
            Add
            <input type="file" accept="image/*" multiple className="sr-only" aria-label="Add photo" onChange={(event) => void addPhotos(event.target.files)} />
          </label>
        </div>
        {photoError ? <p className="mt-1 text-xs text-clinical-danger">{photoError}</p> : null}
      </div>
      <Field label="When" error={errors.checked_at?.message}>
        <Input type="datetime-local" {...form.register('checked_at')} />
      </Field>
      <Field label="Note (optional)" error={errors.note?.message}>
        <Textarea {...form.register('note')} />
      </Field>
      <Footer table="access_checks" id={entry?.id} submitting={isSubmitting} error={errors.root?.message} onDone={onDone} />
    </form>
  );
}
